import { useState, type FormEvent } from 'react';
import type {
  FieldEditPayload,
  SubmitPayload,
} from '../api/types';
import { useProgramConfig } from '../config/useProgramConfig';
import type { ProgramField } from '../config/types';
import { usePostIntake } from '../hooks/usePostIntake';
import { useTranslation } from '../i18n';
import { useIntake } from '../state/IntakeContext';
import { formatFieldValue } from '../utils/formatValue';
import { ThemeToggle } from './ThemeToggle';
import './ReviewScreen.css';

export function ReviewScreen() {
  const config = useProgramConfig();
  const { state, setPhase } = useIntake();
  const { t, localized, lang } = useTranslation();
  const { send, loading, error } = usePostIntake();

  const [editingField, setEditingField] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Order: required fields first, then optional. Within each, iterate the
  // config's key order (insertion-preserving) so the review reads the same
  // way every time.
  const entries = Object.entries(config.fields).filter(([key]) =>
    Object.prototype.hasOwnProperty.call(state.confirmed, key),
  );

  async function onSubmit() {
    setSubmitting(true);
    try {
      await send<SubmitPayload>({
        action: 'submit',
        payload: {} as SubmitPayload,
      });
      setPhase('submitted');
    } catch {
      /* surfaced via `error` */
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main id="main" className="cf-review-screen">
      <header className="cf-review-header">
        <div>
          <h1 className="cf-review-title">{t('review.title')}</h1>
          <p className="cf-review-subtitle">{t('review.subtitle')}</p>
        </div>
        <ThemeToggle />
      </header>

      <ul className="cf-review-list">
        {entries.map(([key, field]) => {
          const value = state.confirmed[key];
          const label = localized(field.label);
          const isEditing = editingField === key;
          return (
            <li key={key} className="cf-review-row">
              <div className="cf-review-row__text">
                <div className="cf-review-row__label">{label}</div>
                {!isEditing && (
                  <div className="cf-review-row__value">
                    {formatFieldValue(field, value, lang)}
                  </div>
                )}
                {isEditing && (
                  <InlineEdit
                    field={field}
                    fieldKey={key}
                    initial={value}
                    onDone={() => setEditingField(null)}
                  />
                )}
              </div>
              {!isEditing && (
                <button
                  type="button"
                  className="cf-btn cf-btn--ghost cf-review-edit-btn"
                  onClick={() => setEditingField(key)}
                  aria-label={t('review.edit_field', { label })}
                >
                  <span aria-hidden="true">✎</span>
                  <span>{t('common.edit')}</span>
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {error && (
        <p className="cf-review-error" role="alert">
          <span aria-hidden="true">⚠</span> {t('chat.error_network')}
        </p>
      )}

      <div className="cf-review-actions">
        <button
          type="button"
          className="cf-btn cf-btn--primary"
          onClick={onSubmit}
          disabled={submitting || loading}
        >
          {submitting || loading
            ? t('review.submitting')
            : t('review.submit')}
        </button>
      </div>
    </main>
  );
}

// ---------------------------------------------------------------------------
// InlineEdit — a single-field editor that fires `field_edit`.
// Reuses each field type's native control for simplicity. Text / number /
// date use plain inputs; select uses a native <select>; multi uses
// checkboxes; boolean uses yes/no buttons.
// ---------------------------------------------------------------------------

function InlineEdit({
  field,
  fieldKey,
  initial,
  onDone,
}: {
  field: ProgramField;
  fieldKey: string;
  initial: unknown;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const { send, loading } = usePostIntake();
  const [value, setValue] = useState<unknown>(initial);

  async function save(e?: FormEvent) {
    if (e) e.preventDefault();
    try {
      await send<FieldEditPayload>({
        action: 'field_edit',
        payload: { field: fieldKey, value },
      });
      onDone();
    } catch {
      /* errors surfaced at parent */
    }
  }

  return (
    <form className="cf-review-edit" onSubmit={save}>
      {field.type === 'text' && (
        <input
          type="text"
          className="cf-input"
          value={String(value ?? '')}
          onChange={(e) => setValue(e.target.value)}
          disabled={loading}
        />
      )}
      {field.type === 'number' && (
        <input
          type="number"
          inputMode="numeric"
          className="cf-input"
          value={String(value ?? '')}
          onChange={(e) =>
            setValue(e.target.value === '' ? '' : Number(e.target.value))
          }
          disabled={loading}
        />
      )}
      {field.type === 'date' && (
        <input
          type="date"
          className="cf-input"
          value={String(value ?? '')}
          onChange={(e) => setValue(e.target.value)}
          disabled={loading}
        />
      )}
      {field.type === 'boolean' && (
        <div className="cf-review-edit__bool">
          <button
            type="button"
            className={
              value === true
                ? 'cf-btn cf-btn--primary'
                : 'cf-btn cf-btn--ghost'
            }
            onClick={() => setValue(true)}
          >
            ✓ {t('common.yes')}
          </button>
          <button
            type="button"
            className={
              value === false
                ? 'cf-btn cf-btn--primary'
                : 'cf-btn cf-btn--ghost'
            }
            onClick={() => setValue(false)}
          >
            ✗ {t('common.no')}
          </button>
        </div>
      )}
      {field.type === 'select' && (
        <select
          className="cf-input"
          value={String(value ?? '')}
          onChange={(e) => setValue(e.target.value)}
          disabled={loading}
        >
          <option value="">—</option>
          {field.options?.map((opt) => (
            <option key={opt.key} value={opt.key}>
              {opt.label.en}
            </option>
          ))}
        </select>
      )}
      {field.type === 'multi' && (
        <div className="cf-review-edit__multi">
          {field.options?.map((opt) => {
            const arr = Array.isArray(value) ? (value as string[]) : [];
            const checked = arr.includes(opt.key);
            return (
              <label key={opt.key} className="cf-review-edit__multi-opt">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    setValue(
                      checked
                        ? arr.filter((k) => k !== opt.key)
                        : [...arr, opt.key],
                    )
                  }
                  disabled={loading}
                />
                {opt.label.en}
              </label>
            );
          })}
        </div>
      )}

      <div className="cf-review-edit__actions">
        <button
          type="submit"
          className="cf-btn cf-btn--primary"
          disabled={loading}
        >
          {t('common.save')}
        </button>
        <button
          type="button"
          className="cf-btn cf-btn--ghost"
          onClick={onDone}
          disabled={loading}
        >
          {t('common.cancel')}
        </button>
      </div>
    </form>
  );
}
